<script setup lang="ts">
import FirstLineIndentBlot from "@/blots/FirstLineIndent";
import { PageBreakBlot } from "@/blots/PageBreakBlot";
import { SelectionBlot } from "@/blots/SelectionBlot";
import { SubmissionPartBlot } from "@/blots/SubmissionPartBlot";
import { Delta, Quill, QuillEditor } from "@vueup/vue-quill";
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import { normalizeDoubleClickWordSelection } from "~/composables/useNormalizeDoubleClickWordSelection";
import { useRegisterAttributors } from "~/composables/quill-selection/useRegisterAttributors";
import { useInitializeToolbar } from "./Toolbar/useInitializeToolbar";
import { useInitializeClipboard } from "./useInitializeClipboard";
import { useInitializeKeyboard } from "./useInitializeKeyboard";
import { useWordProcessorStore } from "./useWordProcessorStore";

const props = defineProps({
  instanceId: {
    type: String,
    required: true,
  },
  modelValue: {
    type: Object as PropType<any>,
    required: true,
  },
  header: {
    type: Object as PropType<any>,
    required: false,
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
  customAttributors: {
    type: Array as PropType<any[]>,
    required: false,
  },
});

const emits = defineEmits([
  "update:modelValue",
  "update:header",
  "onEditorMounted",
  "onUpdateContent",
  "onKeyDown",
  "onMouseMove",
  "onBlur",
  "onTextChange",
  "onKeyUp",
]);

const Parchment = Quill.import("parchment");
const Style = Parchment.Attributor.Style;
const Class = Parchment.Attributor.Class;
const LineHeight = new Style("lineheight", "line-height", {
  scope: Parchment.Scope.BLOCK,
  whitelist: ["1", "1.15", "1.5", "2", "2.5", "3"], // strings
});

const HangingIndent = new Class("hangingIndent", "hanging-indent", {
  scope: Parchment.Scope.BLOCK,
});

// Block-scoped first-line indent
const FirstLine = new Style("firstline", "text-indent", {
  scope: Parchment.Scope.BLOCK,
});

const LeftIndent = new Style("leftIndent", "margin-left", {
  scope: Parchment.Scope.BLOCK,
});

// Inline pixel font-size attributor used by the custom TextSize toolbar control.
const FontSize = new Style("fontSize", "font-size", {
  scope: Parchment.Scope.INLINE,
});

const wordProcessorStore = useWordProcessorStore(props.instanceId);
const { selectionRange, editorRef, quillDelta, shouldShowFormatting } =
  storeToRefs(wordProcessorStore);

// const quillDelta = ref(new Delta());
const scrollOffsetY = ref(0);
const lastUpdatedAt = ref(0);
const pendingDoubleClickSelectionAt = ref<number | null>(null);
provide("quillEditor", editorRef);
provide("instanceId", props.instanceId);

// Child editors initialize and emit ready before the parent's mounted hook.
// Register every saved format during setup, before Quill loads any content.
const registerFormats = () => {
  Quill.register(LineHeight, true);
  Quill.register(HangingIndent, true);
  Quill.register(FirstLine, true);
  Quill.register(LeftIndent, true);
  Quill.register(FontSize, true);
  Quill.register(FirstLineIndentBlot, true);
  Quill.register(SubmissionPartBlot, true);
  Quill.register(SelectionBlot, true);
  Quill.register(PageBreakBlot, true);

  if (props.customAttributors) {
    for (const attribute of props.customAttributors) {
      useRegisterAttributors().registerDataAttributor(
        attribute.attribute,
        attribute.attribute,
        attribute.scope
      );
    }
  }
};

registerFormats();

onMounted(async () => {
  wordProcessorStore.resetEditorState();

  await nextTick();

  const editorElement = editorRef.value?.editor;
  if (editorElement == undefined) return;

  const quillEditorElement = editorElement.querySelector(".ql-editor");
  if (quillEditorElement == undefined) return;

  quillEditorElement.addEventListener("dblclick", onEditorDoubleClick);
  quillEditorElement.addEventListener("scroll", (e: any) => {
    scrollOffsetY.value = e.target.scrollTop;
  });
});

const onReady = (quill: any) => {
  if (props.readOnly == true) {
    wordProcessorStore.setEditorReady(true);
    emits("onEditorMounted");
    return;
  }

  // Add custom backspace handler at the beginning of the bindings array
  // so it runs BEFORE Quill's default backspace handlers
  useInitializeKeyboard(quill);
  useInitializeToolbar(quill);
  useInitializeClipboard(quill);
  const history = quill.getModule("history");
  if (history) {
    history.options.userOnly = true;
    history.clear();
  }
  wordProcessorStore.setEditorReady(true);
  emits("onEditorMounted");
};

onUnmounted(() => {
  const editorElement = editorRef.value?.editor;
  const quillEditorElement = editorElement?.querySelector(".ql-editor");
  quillEditorElement?.removeEventListener("dblclick", onEditorDoubleClick);
  wordProcessorStore.resetEditorState();
});

const quillToolbarId = ref(`quill-toolbar-${useGenerateUID()}`);
const toolbar = computed(() => {
  if (props.readOnly == true) {
    return [];
  }

  return `#${quillToolbarId.value}`;
});

const onEditorDoubleClick = () => {
  pendingDoubleClickSelectionAt.value = Date.now();
};

const onSelectionChange = () => {
  const quill = editorRef.value?.getQuill?.();
  if (!quill) return;

  let range = quill.getSelection();

  if (!range) return;

  const hasPendingDoubleClickSelection =
    pendingDoubleClickSelectionAt.value != null &&
    Date.now() - pendingDoubleClickSelectionAt.value < 400;

  if (hasPendingDoubleClickSelection && range.length > 0) {
    const normalizedRange = normalizeDoubleClickWordSelection(quill, range);

    if (
      normalizedRange &&
      (normalizedRange.index !== range.index ||
        normalizedRange.length !== range.length)
    ) {
      quill.setSelection(
        normalizedRange.index,
        normalizedRange.length,
        Quill.sources.SILENT
      );
      range = normalizedRange;
    }
  }

  pendingDoubleClickSelectionAt.value = null;
  selectionRange.value = range;
  // Get the x/y coordinates of the first piece of text in the selection range

  if (range.length == 0) return;

  selectionRange.value = {
    index: range.index,
    length: range.length,
  };
};

const baseFormats = [
  "bold",
  "italic",
  "underline",
  "strike",
  "header",
  "size",
  "fontSize",
  "color",
  "background",
  "link",
  "image",
  "video",
  "section-header",
  "table-of-contents",
  "imageUpload",
  "list", // For ordered and bullet lists
  "indent", // For indentation
  "width",
  "height",
  "align",
  "lineheight",
  "firstline",
  "leftIndent",
  "firstline-indent",
  "hangingIndent",
  "submissionPart",
  "selectionBlot",
  "pageBreak",
  "data-banned-word",
];

// Keyboard bindings and editor module options
const editorOptions = computed(() => {
  // Include dynamically registered custom attributes
  const customAttributes =
    props.customAttributors?.map((attr) => attr.attribute) ?? [];

  let allFormats = baseFormats;

  if (
    props.customAttributors?.length != undefined &&
    props.customAttributors?.length > 0
  ) {
    allFormats = [...baseFormats, ...customAttributes];
  }

  return {
    formats: allFormats,
    modules: {
      keyboard: {
        bindings: {
          // We'll add custom bindings programmatically in onReady
          // but we need to initialize the keyboard module
        },
      },
    },
  };
});

const onUpdateContent = (content: Delta) => {
  lastUpdatedAt.value = Date.now();
  quillDelta.value = content;
  emits("onUpdateContent", content);
};

const onMouseMove = (event: MouseEvent) => {
  emits("onMouseMove", event);
};
</script>

<template>
  <div
    class="word-processor-container flex flex-col h-full min-h-0 overflow-hidden"
    :class="{
      'read-only': readOnly,
      'show-formatting': shouldShowFormatting,
    }"
  >
    <BaseWordProcessorToolbar
      v-if="readOnly != true"
      :toolbar-id="quillToolbarId"
    >
      <template #actions>
        <slot name="toolbar-actions" />
      </template>
    </BaseWordProcessorToolbar>
    <div
      class="grow min-h-0 flex flex-col shadow overflow-hidden rounded rounded-bl-none rounded-br-none border"
    >
      <slot name="fixed-header" />
      <div
        class="grow flex flex-col min-h-0 overflow-auto relative page-container"
      >
        <BaseWordProcessorHeader
          :model-value="header"
          :read-only="readOnly"
          @update:modelValue="$emit('update:header', $event)"
        />
        <slot name="overlays" />
        <QuillEditor
          ref="editorRef"
          @ready="onReady"
          :content="modelValue"
          :toolbar="toolbar"
          :options="editorOptions"
          :read-only="readOnly"
          :enable="!readOnly"
          @update:content="onUpdateContent"
          class="h-full"
          @selection-change="onSelectionChange"
          @text-change="$emit('onTextChange', $event)"
          @keydown="$emit('onKeyDown', $event)"
          @keyup="$emit('onKeyUp', $event)"
          @mousemove="onMouseMove"
          @blur="$emit('onBlur')"
        />
      </div>
    </div>
  </div>
</template>

<style>
.word-processor-container .page-break {
  @apply border-t border-b  bg-light w-full;
  height: 6px;
  margin: 24px 0;
}

.word-processor-container .ql-editor {
  tab-size: 2em; /* Number of spaces a tab represents */
  -moz-tab-size: 16; /* For Firefox */
}

.word-processor-container .start-highlight {
  @apply border-l-2 border-l-primary rounded-l-md;
}

.word-processor-container .end-highlight {
  @apply border-r-2 border-r-primary rounded-r-md;
}

.word-processor-container .ql-picker-label {
  @apply rounded !w-[30px] !h-[30px];
  width: 30px !important;
  height: 30px !important;
  padding: 6px !important;
}
.word-processor-container .ql-picker-options {
  @apply rounded;
}

.word-processor-container .ql-align {
  .ql-picker-item {
    @apply rounded;
    width: 30px !important;
    height: 30px !important;
    padding: 6px !important;
  }
}

.word-processor-container .ql-toolbar.ql-snow + .ql-container.ql-snow {
  border-top: unset;
}

.word-processor-container .ql-container.ql-snow {
  border: unset;
}

.word-processor-container .ql-container {
  font-size: 12pt !important;
}

.word-processor-container .ql-editor,
.word-processor-container .ql-container {
  font-family: "Times New Roman", serif !important;
  @apply bg-transparent !important;
}

.word-processor-container .ql-editor {
  padding: 48px 48px !important;
  padding-top: 0 !important;
  overflow: visible !important;
  max-height: none !important;
  height: auto !important;
}

.word-processor-container.show-formatting .ql-editor > p::after {
  content: "\00B6";
  color: rgb(220 38 38 / 0.8);
  display: inline;
  margin-left: 0.2em;
  pointer-events: none;
  user-select: none;
}

.word-processor-container.show-formatting .ql-editor > p:has(> br:only-child) {
  position: relative;
}

.word-processor-container.show-formatting
  .ql-editor
  > p:has(> br:only-child)::after {
  position: absolute;
  top: 0;
  left: 0.45em;
  margin-left: 0;
}

.word-processor-container .disabled .ql-toolbar,
.word-processor-container .read-only .ql-toolbar {
  display: none;
}

.word-processor-container .disabled #quill-toolbar,
.word-processor-container .read-only #quill-toolbar {
  margin-bottom: 0px;
}

.word-processor-container .ql-snow.ql-toolbar button,
.word-processor-container .ql-snow .ql-toolbar button {
  width: 30px !important;
  height: 30px !important;
  padding: 0px;
  @apply rounded;
}

.word-processor-container .ql-snow .ql-active {
  @apply bg-primary !important;
  @apply text-primary !important;
  @apply bg-opacity-20 !important;

  path,
  line {
    @apply stroke-primary !important;
  }
}

.word-processor-container .read-only .ql-container,
.word-processor-container .read-only .ql-editor,
.word-processor-container .read-only .editor-container {
  @apply bg-transparent;

  @apply text-body-1;

  @apply p-0 !important;
  border: none !important;
  font-size: 12pt !important;
}

.word-processor-container .disabled .ql-container,
.word-processor-container .disabled .ql-editor {
  @apply !text-[#030202];
}

.word-processor-container .image-uploading-container {
  position: relative;
  display: inline-block;
}

.word-processor-container .image-uploading-container .progress-bar {
  height: 8px;
  @apply bg-success-default;
  width: 0%;
  @apply mb-2;
  @apply rounded;
}

.word-processor-container .image-uploading-container img {
  max-width: 100%;
}

.word-processor-container .ql-editor .ql-lineheight-100 {
  line-height: 1;
}
.word-processor-container .ql-editor .ql-lineheight-115 {
  line-height: 1.15;
}
.word-processor-container .ql-editor .ql-lineheight-150 {
  line-height: 1.5;
}
.word-processor-container .ql-editor .ql-lineheight-200 {
  line-height: 2;
}
.word-processor-container .ql-editor .ql-lineheight-250 {
  line-height: 2.5;
}
.word-processor-container .ql-editor .ql-lineheight-300 {
  line-height: 3;
}

.word-processor-container .ql-clipboard {
  position: fixed !important;
  opacity: 0 !important;
  left: 50% !important;
  top: 50% !important;
}

.hanging-indent-1 {
  padding-left: 2em !important;
  text-indent: -2em;
}

.word-processor-container.read-only .ql-toolbar {
  display: none !important;
}
</style>
